import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../css/JubileeWidget.css';
import JubileeDetailModal from './JubileeDetailModal';
import api from './api';

const JubileeWidget = ({ onViewAllClick }) => {
  const [jubilees, setJubilees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJubilee, setSelectedJubilee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Define milestone years for jubilees
  const milestones = [5, 10, 12.5, 25, 40, 50, 60, 65, 70];

  // Parse date string to Date object, handling multiple formats
  const parseDate = (dateString) => {
    if (!dateString || dateString.trim() === '') return null;
    
    let dateParts;
    let day, month, year;
    
    // Handle different delimiters
    if (dateString.includes('-')) {
      dateParts = dateString.split('-');
    } else if (dateString.includes('/')) {
      dateParts = dateString.split('/');
    } else {
      return null; // Unrecognized format
    }
    
    if (dateParts.length !== 3) return null;
    
    // Determine format - could be DD-MM-YYYY, YYYY-MM-DD, etc.
    if (dateParts[0].length === 4) {
      // YYYY-MM-DD
      year = parseInt(dateParts[0]);
      month = parseInt(dateParts[1]) - 1; // JS months are 0-indexed
      day = parseInt(dateParts[2]);
    } else {
      // DD-MM-YYYY or MM-DD-YYYY
      // For VVE purposes, assume European format: DD-MM-YYYY
      day = parseInt(dateParts[0]);
      month = parseInt(dateParts[1]) - 1; // JS months are 0-indexed
      year = parseInt(dateParts[2]);
    }
    
    // Validate date components
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (year < 1900 || year > 2100) return null; // Basic sanity check
    if (month < 0 || month > 11) return null;
    if (day < 1 || day > 31) return null;
    
    return new Date(year, month, day);
  };

  // Calculate future jubilee date and years from now
  const calculateJubilee = (startDate, years) => {
    const startDateObj = typeof startDate === 'string' ? parseDate(startDate) : startDate;
    if (!startDateObj) return null;
    
    const jubileeDate = new Date(startDateObj);
    
    // For 12.5 years, add 12 years and 6 months
    if (years === 12.5) {
      jubileeDate.setFullYear(startDateObj.getFullYear() + 12);
      jubileeDate.setMonth(startDateObj.getMonth() + 6);
    } else {
      jubileeDate.setFullYear(startDateObj.getFullYear() + years);
    }
    
    return jubileeDate;
  };

  // Calculate days until jubilee
  const daysUntil = (date) => {
    const today = new Date();
    const differenceMs = date - today;
    return Math.ceil(differenceMs / (1000 * 60 * 60 * 24));
  };

  // Format date to Dutch format
  const formatDate = (date) => {
    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };


 
  useEffect(() => {
    const fetchBewoners = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/bewoners/get-all');
        const bewoners = response.data;
        
        // Filter out former residents
        const currentBewoners = bewoners.filter(bewoner => 
          !bewoner.isFormerResident && 
          (!bewoner.einddatum || bewoner.einddatum.trim() === '')
        );
        
        // Process each resident to find upcoming jubilees
        const allJubilees = [];
        
        currentBewoners.forEach(bewoner => {
          // Skip if no ingangsdatum
          if (!bewoner.ingangsdatum || bewoner.ingangsdatum.trim() === '') return;
          
          try {
            // Parse the date using our helper function
            const startDate = parseDate(bewoner.ingangsdatum);
            if (!startDate) return; // Skip if date couldn't be parsed
            
            // Calculate future jubilees for each milestone
            milestones.forEach(years => {
              const jubileeDate = calculateJubilee(startDate, years);
              
              // Only include future jubilees within the next 365 days
              const daysToJubilee = daysUntil(jubileeDate);
              if (daysToJubilee > 0 && daysToJubilee <= 900) {
                allJubilees.push({
                  naam: bewoner.naam,
                  nummer: bewoner.nummer,
                  jubileeType: years,
                  jubileeDate: jubileeDate,
                  daysUntil: daysToJubilee
                });
              }
            });
          } catch (error) {
            console.error(`Error processing date for bewoner ${bewoner.naam}:`, error);
          }
        });
        
        // Group jubilees by house number and jubilee date
        const groupedJubilees = [];
        const jubileeMap = {};
        
        allJubilees.forEach(jubilee => {
          // Create a unique key for each house number + jubilee type + date
          const key = `${jubilee.nummer}_${jubilee.jubileeType}_${jubilee.jubileeDate.toISOString().split('T')[0]}`;
          
          if (!jubileeMap[key]) {
            // First person at this address with this jubilee
            jubileeMap[key] = {
              ...jubilee,
              residents: [jubilee.naam]
            };
            groupedJubilees.push(jubileeMap[key]);
          } else {
            // Additional person at same address with same jubilee
            jubileeMap[key].residents.push(jubilee.naam);
            // Update the name to show multiple residents
            jubileeMap[key].naam = jubileeMap[key].residents.join(' & ');
          }
        });
        
        // Sort jubilees by date (closest first)
        groupedJubilees.sort((a, b) => a.daysUntil - b.daysUntil);
        
        // Increase to the closest 6 jubilees (from grouped results)
        setJubilees(groupedJubilees.slice(0, 5));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching bewoners:', err);
        setError('Er is een fout opgetreden bij het ophalen van de bewonersinformatie.');
        setLoading(false);
      }
    };
  
    fetchBewoners();
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="jubilee-widget card">
      <h3>Komende Jubilea</h3>
      
      {loading && <p>Laden...</p>}
      
      {error && <p className="error-message">{error}</p>}
      
      {!loading && !error && jubilees.length === 0 && (
        <p>Geen jubilea gepland in het komende jaar.</p>
      )}
      
      {!loading && !error && jubilees.length > 0 && (
        <ul className="jubilee-list">
          {jubilees.map((jubilee, index) => (
            <li 
              key={index} 
              className={`jubilee-item ${jubilee.daysUntil <= 30 ? 'soon' : ''}`}
              onClick={() => {
                setSelectedJubilee(jubilee);
                setShowDetailModal(true);
              }}
            >
              <div className="jubilee-content">
                <div className="jubilee-header">
                  <span className="jubilee-name">{jubilee.naam}</span>
                  <span className="jubilee-apartment">Nr. {jubilee.nummer}</span>
                </div>
                <div className="jubilee-details">
                  <span className={`jubilee-years ${
                    jubilee.jubileeType === 25 || jubilee.jubileeType === 50 ? 'special' : ''}`}
                  >
                    {jubilee.jubileeType === 12.5 ? '12½' : jubilee.jubileeType} jaar
                  </span>
                  <span className="jubilee-date">
                    {formatDate(jubilee.jubileeDate)}
                  </span>
                  <span className="jubilee-countdown">
                    {jubilee.daysUntil === 0 ? (
                      <span className="today">Vandaag!</span>
                    ) : jubilee.daysUntil === 1 ? (
                      'Morgen'
                    ) : jubilee.daysUntil <= 7 ? (
                      <strong>Over {jubilee.daysUntil} dagen</strong>
                    ) : jubilee.daysUntil <= 30 ? (
                      `Over ${jubilee.daysUntil} dagen`
                    ) : (
                      `Over ${Math.floor(jubilee.daysUntil / 30)} maanden`
                    )}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      
      <div className="jubilee-footer">
      <p>Bewoners mijlpalen: 5, 10, 12½, 25, 40, 50, 60, 65 en 70 jaar.</p>
       
      </div>
      
      {/* Detail Modal */}
      <JubileeDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        jubilee={selectedJubilee}
      />
    </div>
  );
};

export default JubileeWidget;